import { CityMultiLingualDTO, Street } from "@lg-cfa/services";
import { Broker } from "@lg-cfa/types";
import { atom, selector } from "recoil";

import { GetTariffParameterObjectBuildingType, UserType } from "@enums";
import { persistAtom } from "@states/persist";
import { Insurance, UserDataInterface, WtcReason } from "@types";

export const userTypeState = atom<`${UserType}` | undefined>({
    key: "userType",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const weTakeCareActivated = atom<boolean>({
    key: "weTakeCareActivated",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const weTakeCareStepout = atom<boolean>({
    key: "weTakeCareStepout",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const housingTypeState = atom<
    GetTariffParameterObjectBuildingType | undefined
>({
    key: "housingTypeState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const propertyStreetState = atom<Street | undefined>({
    key: "propertyStreetState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const propertyNumberState = atom<string>({
    key: "propertyNumberState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const propertyLetterBoxState = atom<string>({
    key: "propertyLetterBoxState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const propertyPostalCodeState = atom<string>({
    key: "propertyPostalCodeState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const propertyCityState = atom<CityMultiLingualDTO | undefined>({
    key: "propertyCityState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const propertyRisk = atom<number>({
    key: "propertyRisk",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});

export const currentOtherAmount = atom<number>({
    key: "currentOtherAmount",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});

export const currentAtticsAmount = atom<number>({
    key: "currentAtticsAmount",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});
export const currentGaragesAmount = atom<number>({
    key: "currentGaragesAmount",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});
export const currentCellarsAmount = atom<number>({
    key: "currentCellarsAmount",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});
export const currentExtrasAmount = atom<number>({
    key: "currentExtrasAmount",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});

export const insuranceState = atom<Insurance | undefined>({
    key: "insuranceState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const isTheftCheckedState = atom<boolean>({
    key: "isTheftCheckedState",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const isLegalCheckedState = atom<boolean>({
    key: "isLegalCheckedState",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const isPackageCheckedState = atom<boolean>({
    key: "isPackageCheckedState",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const chosenTheftOptionState = atom<string>({
    key: "chosenTheftOptionState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const totalPriceState = atom<number>({
    key: "totalPriceState",
    default: 0,
    effects_UNSTABLE: [persistAtom],
});

export const brokerDataState = atom<Broker | undefined>({
    key: "brokerDataState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationFirstNameState = atom<string>({
    key: "yourInformationFirstNameState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationLastNameState = atom<string>({
    key: "yourInformationLastNameState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationEmailState = atom<string>({
    key: "yourInformationEmailState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationPhoneState = atom<string>({
    key: "yourInformationPhoneState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationQuestionState = atom<string>({
    key: "yourInformationQuestionState",
    default: "",
    effects_UNSTABLE: [persistAtom],
});

export const yourInformationConsentState = atom<boolean>({
    key: "yourInformationConsentState",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const securityDoorState = atom<string>({
    key: "securityDoorState",
    default: "no-door",
    effects_UNSTABLE: [persistAtom],
});

export const flowFinishedState = atom<boolean>({
    key: "flowFinishedState",
    default: false,
    effects_UNSTABLE: [persistAtom],
});

export const weTakeCareReason = atom<WtcReason>({
    key: "weTakeCareReason",
    default: {
        reason: undefined,
        page: undefined,
        businessRule: undefined,
    },
    effects_UNSTABLE: [persistAtom],
});

export const thankYouDataState = atom({
    key: "thankYouDataState",
    default: undefined,
    effects_UNSTABLE: [persistAtom],
});

export const userDataState = selector({
    key: "userData",
    get: ({ get }): UserDataInterface => {
        const total =
            (get(currentOtherAmount) ?? 0) +
            (get(currentAtticsAmount) ?? 0) +
            (get(currentGaragesAmount) ?? 0) +
            (get(currentCellarsAmount) ?? 0) +
            (get(currentExtrasAmount) ?? 0);

        return {
            housingType: get(housingTypeState),
            propertyStreet: get(propertyStreetState),
            propertyNumber: get(propertyNumberState),
            propertyLetterBox: get(propertyLetterBoxState),
            propertyPostalCode: get(propertyPostalCodeState),
            propertyCity: get(propertyCityState),
            propertyRisk: get(propertyRisk),
            others: get(currentOtherAmount),
            attics: get(currentAtticsAmount),
            garages: get(currentGaragesAmount),
            cellars: get(currentCellarsAmount),
            extras: get(currentExtrasAmount),
            totalRoomCount: total,
            totalPrice: get(totalPriceState),
            insurance: get(insuranceState),
            isTheftChecked: get(isTheftCheckedState),
            isLegalChecked: get(isLegalCheckedState),
            isPackageChecked: get(isPackageCheckedState),
            chosenTheftOption: get(chosenTheftOptionState),
            brokerData: get(brokerDataState),
            yourInformationFirstName: get(yourInformationFirstNameState),
            yourInformationLastName: get(yourInformationLastNameState),
            yourInformationEmail: get(yourInformationEmailState),
            yourInformationPhone: get(yourInformationPhoneState),
            yourInformationQuestion: get(yourInformationQuestionState),
            securityDoorState: get(securityDoorState),
            yourInformationConsent: get(yourInformationConsentState),
            weTakeCareActivated: get(weTakeCareActivated),
            weTakeCareStepout: get(weTakeCareStepout),
            flowFinished: get(flowFinishedState),
            weTakeCareReason: get(weTakeCareReason),
            thankYouData: get(thankYouDataState),
        };
    },
});
